import { FormInputProps } from '@acorns/web-components/dist/FormBuilder/types'
import ApolloClient from 'apollo-client'
import jsonata from 'jsonata'
import { findLast, pathOr } from 'ramda'

import getUser from 'src/graphql/queries/userDetails.query.gql'
import { incomeMask } from 'utils/text-masks/income'

import getCoreSuitabilityQuestions from './graphql/coreSuitabilityQuestions.query.gql'
import getLaterSuitabilityState from './graphql/getLaterSuitabilityState.query.gql'
import laterQuestionsJSON from './later_tree.json'
import { QuestionFlowType, StoredQuestion, SuitabilityState } from './types'

// Declaring page 1 and page 2 Core Suitability Question keys
const page1CoreSuitabilityQuestionKeys = ['net_worth', 'financial_education']

const page2CoreSuitabilityQuestionKeys = [
  'investment_goal',
  'initial_investment',
  'investment_access',
]

// Retrieves a JSON fragment based on a path expression
// Invalid paths return undefined
export const evaluatePath = (questionPath: string) => {
  return jsonata(questionPath).evaluate(laterQuestionsJSON)
}

// Saves a StoredQuestion instance in local state
export const saveQuestion = (
  client: ApolloClient<any>,
  question: StoredQuestion,
  questionPath: string,
  page: number,
) => {
  // If a questionPath was provided, evaluate the path and construct a StoredQuestion
  if (questionPath) {
    const questionFragment = evaluatePath(questionPath)
    question = {
      questionId: questionFragment.id,
      answerId: '',
      questionPath,
      page,
      flowType: QuestionFlowType.Later,
      __typename: 'StoredQuestion',
    }
  }

  // Query for existing StoredQuestion items, add the question
  // and write to local state
  const { storedQuestions } = getLocalState(client)
  storedQuestions.push(question)

  client.cache.writeData({ data: { storedQuestions } })
}

// Converts a question JSON fragment to a FormRow
export const toFormRow = (questionFragment: any, client: ApolloClient<any>): FormInputProps => {
  // Extract form row properties from JSON fragment
  const type = questionFragment.type === 'multi' ? 'select' : 'text'

  const props = {
    label: questionFragment.title,
    name: questionFragment.id,
    type,
    sizes: { xs: 12, sm: 12, md: 12 },
  }

  if (type === 'select') {
    // Add select-specific form row props and return
    const options = questionFragment.answers.map(answer => {
      return {
        label: answer.title,
        value: answer.id,
      }
    })

    // Determine position of options pop-up.  The first two SelectInput
    // instances will render the Options component below the SelectInput,
    // and the rest will render above.
    const { storedQuestions, page } = getLocalState(client)
    const numQuestions = storedQuestions.filter(question => question.page === page).length
    const position = numQuestions < 2 ? 'bottom' : 'top'

    props.options = options
    props.useNative = false
    props.position = position
  } else if (type === 'text') {
    props.mask = incomeMask
  }

  return props
}

// Converts GQL response into an array of FormRowProps
export const mapRowsAndQuestions = (responses, client: ApolloClient<any>) => {
  // Determine position of options pop-up.  The first two SelectInput
  // instances will render the Options component below the SelectInput,
  // and the rest will render above.
  const { storedQuestions, page } = getLocalState(client)
  const numQuestions = storedQuestions.filter(question => question.page === page).length
  const position = numQuestions < 2 ? 'bottom' : 'top'

  const questions = responses
    ? responses.map(response => {
        return {
          label: pathOr('', ['question', 'text'], response),
          name: pathOr('', ['question', 'id'], response),
          options: pathOr([], ['question', 'possibleAnswers'], response).map(answer => {
            return {
              label: pathOr('', ['text'], answer),
              value: pathOr('', ['id'], answer),
              subtext: pathOr(null, ['description'], answer),
            }
          }),
          type: 'select',
          useNative: false,
          sizes: { xs: 12, sm: 12, md: 12 },
          position,
        }
      })
    : []

  return questions
}

// Asynchronous function that retrieves Core Suitability questions and stores them
// in local state.
export const initializeSuitabilityState = async (client: ApolloClient<any>) => {
  const { data } = await client.query({ query: getCoreSuitabilityQuestions })
  const {
    data: { user },
  } = await client.query({ query: getUser })
  const coreSuitabilityQuestions = data.suitabilityResponses
  const registrationComplete = user.registrationComplete ? true : false

  // Restore all data
  client.cache.writeData({
    data: {
      storedQuestions: [],
      recommendation: null,
      coreSuitabilityQuestions,
      showCoreSuitabilityQuestions: false,
      page: 1,
      showNextButton: false,
      existingCoreCustomer: registrationComplete,
    },
  })
}

// Retrieve all local state data
export const getLocalState = (client: ApolloClient<any>): SuitabilityState => {
  const data: SuitabilityState = client.cache.readQuery({ query: getLaterSuitabilityState })

  return {
    storedQuestions: data.storedQuestions,
    recommendation: data.recommendation,
    coreSuitabilityQuestions: data.coreSuitabilityQuestions,
    showCoreSuitabilityQuestions: data.showCoreSuitabilityQuestions,
    page: data.page,
    showNextButton: data.showNextButton,
    existingCoreCustomer: data.existingCoreCustomer,
  }
}

// Updates local state based on StoredQuestion items already in local state
// This should be called each time an answer is updated
export const updateShowCoreSuitabilityState = (client: ApolloClient<any>): SuitabilityState => {
  const data = getLocalState(client)
  const { storedQuestions } = data
  let showCoreSuitabilityQuestions = false

  // Determine the state of Core Suitability questions
  if (
    storedQuestions.filter(storedQuestion => storedQuestion.flowType === QuestionFlowType.Core)
      .length > 0
  ) {
    // Some Core Suitability questions exist in local storage
    // If they aren't all answered, then we need to show Core Suitability
    // questions
    if (!areAllCoreSuitabilityQuestionsAnswered(client)) {
      showCoreSuitabilityQuestions = true
    }
  } else {
    // No Core Suitability questions exist in local storage
    // Evaluate the most recent path to determine if the customer should enter
    // the Core Suitability questions flow
    let currentQuestionPath = storedQuestions[storedQuestions.length - 1].questionPath
    if (currentQuestionPath) {
      // Current question is a Later question, so determine the next path
      const questionParts = currentQuestionPath.split('.')
      if (questionParts.length > 1) {
        currentQuestionPath = questionParts.slice(0, questionParts.length - 1).join('.')
      }
      if (getNextPath(currentQuestionPath, client) === 'showCoreSuitabilityQuestions') {
        showCoreSuitabilityQuestions = true
      }
    } else {
      // Current question is a Core Suitability question
      showCoreSuitabilityQuestions = !areAllCoreSuitabilityQuestionsAnswered(client)
    }
  }

  // Save state
  client.cache.writeData({
    data: {
      showCoreSuitabilityQuestions,
    },
  })

  return {
    ...data,
    showCoreSuitabilityQuestions,
  }
}

// Sets the page state in local state
export const setNextPage = (client: ApolloClient<any>) => {
  const { page } = getLocalState(client)

  if (page === 1) {
    client.cache.writeData({
      data: {
        showNextButton: false,
        page: 2,
      },
    })
  } else if (page === 2) {
    client.cache.writeData({
      data: {
        showNextButton: false,
        page: null,
      },
    })
  }
}

export const areAllCoreSuitabilityQuestionsAnswered = (client: ApolloClient<any>): boolean => {
  // Determine the full set of question keys.
  const coreSuitabilityQuestionKeys = page1CoreSuitabilityQuestionKeys.concat(
    page2CoreSuitabilityQuestionKeys,
  )
  const { coreSuitabilityQuestions } = getLocalState(client)

  for (const key of coreSuitabilityQuestionKeys) {
    // Resolve key to a Core Suitability Question, then check if an answer exists
    const coreSuitabilityQuestion = coreSuitabilityQuestions.find(
      question => question.question.key === key,
    )

    if (getAnswer(client, coreSuitabilityQuestion.question.id) === '') {
      return false
    }
  }

  return true
}

// Given a questionId, looks through local state and updates the matching
// StoredQuestion with the provided answerId.  If the question already
// exists, subsequent questions are removed from local state.
export const updateAnswer = (
  client: ApolloClient<any>,
  questionId: string,
  answerId: string = null,
) => {
  // Retrieve existing state
  const suitabilityState = getLocalState(client)

  // Reset suitability and recommendation state
  client.cache.writeData({
    data: {
      ...suitabilityState,
      showCoreSuitabilityQuestions: false,
      recommendation: null,
      showNextButton: false,
    },
  })

  // Update answer value for the answered question, if it already exists in
  // local state.  Truncate any items after the answered question
  let { showCoreSuitabilityQuestions } = suitabilityState
  const { storedQuestions } = suitabilityState
  const i = storedQuestions.findIndex(storedQuestion => storedQuestion.questionId === questionId)

  if (i !== -1) {
    storedQuestions[i].answerId = answerId
    storedQuestions.length = i + 1

    client.cache.writeData({
      data: {
        storedQuestions,
      },
    })

    // When a question is updated, we always have to determine whether the
    // customer is still in the suitability flow or not
    showCoreSuitabilityQuestions = updateShowCoreSuitabilityState(client)
      .showCoreSuitabilityQuestions

    // Write data back into cache
    client.cache.writeData({
      data: {
        storedQuestions,
        showCoreSuitabilityQuestions,
      },
    })
  }
}

// For a given questionId, search local state and return an answer (if one exists)
export const getAnswer = (client: ApolloClient<any>, questionId: string): string => {
  // Query for existing StoredQuestion items
  const { storedQuestions } = getLocalState(client)
  const i = storedQuestions.findIndex(a => a.questionId === questionId)

  return i !== -1 ? storedQuestions[i].answerId : ''
}

export const addCoreSuitabilityQuestions = (client: ApolloClient<any>, currentRows: any) => {
  // Retrieve local state of suitability questions, including what page the
  // customer is on
  const suitabilityState = getLocalState(client)
  const { coreSuitabilityQuestions, showCoreSuitabilityQuestions, page } = suitabilityState

  // Do not process further rows if the customer is not in the Core Suitability portion
  // of the flow
  if (!showCoreSuitabilityQuestions) {
    return currentRows
  }

  const page1CoreSuitabilityQuestionIds = coreSuitabilityQuestions
    .filter(question => page1CoreSuitabilityQuestionKeys.includes(question.question.key))
    .map(question => question.question.id)

  const page2CoreSuitabilityQuestionIds = coreSuitabilityQuestions
    .filter(question => page2CoreSuitabilityQuestionKeys.includes(question.question.key))
    .map(question => question.question.id)

  // Set the source of question IDs based on the page that the customer is on
  const questionIds = page === 1 ? page1CoreSuitabilityQuestionIds : page2CoreSuitabilityQuestionIds

  for (const questionId of questionIds) {
    // If an answer to the question is not found, save it as a Stored Question,
    // convert it to a FormRow, add to to current rows and return it
    if (getAnswer(client, questionId) === '') {
      const coreSuitabilityQuestion = coreSuitabilityQuestions.filter(
        ({ question }) => question.id === questionId,
      )
      const addedRow = mapRowsAndQuestions(coreSuitabilityQuestion, client)

      // Store this question in local state
      const newQuestion: StoredQuestion = {
        questionId: addedRow[0].name,
        answerId: '',
        questionPath: null,
        page,
        flowType: QuestionFlowType.Core,
        __typename: 'StoredQuestion',
      }

      saveQuestion(client, newQuestion, null, page)

      return currentRows.concat(addedRow)
    }
  }

  // If execution has reached this point, then all the core suitability
  // questions for the current page have been answered.  If the customer
  // is on page 1, then show the "Next" button.  If the customer is on
  // page 2, then don't show the "Next" button, and mark questions as
  // completed
  client.cache.writeData({
    data: {
      showNextButton: page === 1,
      showCoreSuitabilityQuestions: page === 1,
    },
  })

  return currentRows
}

export const getRows = (client: ApolloClient<any>) => {
  let currentQuestionPath = ''
  let nextQuestionPath = ''

  // Query for existing local state
  const {
    coreSuitabilityQuestions,
    showCoreSuitabilityQuestions,
    storedQuestions,
    page,
    recommendation,
    existingCoreCustomer,
  } = getLocalState(client)

  // Construct a new set of FormRows
  let newFormRows = []

  // Retrieve all existing rows for the current page
  storedQuestions
    .filter(storedQuestion => storedQuestion.page === page)
    .forEach(storedQuestion => {
      if (storedQuestion.questionPath) {
        // Items with a JSON question path are Later Suitability
        newFormRows.push(toFormRow(evaluatePath(storedQuestion.questionPath), client))
      } else {
        // Items that do not have a JSON question path are Core Suitability
        const coreSuitabilityQuestion = coreSuitabilityQuestions.filter(
          ({ question }) => question.id === storedQuestion.questionId,
        )
        newFormRows.push(mapRowsAndQuestions(coreSuitabilityQuestion, client)[0])
      }
    })

  // If no answered questions exist, default to the first question
  if (storedQuestions.length === 0) {
    // Store default question in local storage and return as a FormRow
    currentQuestionPath = '$.questions[0]'
    saveQuestion(client, null, currentQuestionPath, 1)
    return [[toFormRow(evaluatePath(currentQuestionPath), client)]]
  }

  // If customer is in the middle of entering Core Suitability questions
  // then continue with that path
  if (showCoreSuitabilityQuestions && !existingCoreCustomer) {
    newFormRows = addCoreSuitabilityQuestions(client, newFormRows)
    return [newFormRows]
  }

  // If questions exist, we have to evaluate the current path to see if
  // it results in another question, recommendation, or Core Suitability questions
  currentQuestionPath = storedQuestions[storedQuestions.length - 1].questionPath

  // If the most recent question is the first question, change the search path
  // to start at the root of the document
  if (['$.questions[0]', '$.questions[1]'].includes(currentQuestionPath)) {
    currentQuestionPath = '$'
  } else if (!currentQuestionPath) {
    if (recommendation) {
      // We have a recommendation and the full path has been traversed
      // Update state and return rows
      client.cache.writeData({
        data: {
          showCoreSuitabilityQuestions: false,
          page: 2,
          showNextButton: true,
        },
      })

      return [newFormRows]
    } else {
      // Must continue processing from the previous path
      const questionParts = findLast(
        storedQuestion => storedQuestion.questionPath !== null,
        storedQuestions,
      ).questionPath.split('.')

      if (questionParts.length > 1) {
        currentQuestionPath = questionParts.slice(0, questionParts.length - 1).join('.')
      }
    }
  } else {
    // Strip off the last element of the path to resolve to a child node
    // which will be used in the recursive call
    const questionParts = currentQuestionPath.split('.')
    if (questionParts.length > 1) {
      currentQuestionPath = questionParts.slice(0, questionParts.length - 1).join('.')
    }
  }

  nextQuestionPath = getNextPath(currentQuestionPath, client)

  // Check for path termination
  if (nextQuestionPath === '') {
    client.cache.writeData({ data: { showNextButton: true } })
    return [newFormRows]
  }

  if (nextQuestionPath === 'showCoreSuitabilityQuestions') {
    client.cache.writeData({ data: { showCoreSuitabilityQuestions: true } })
    newFormRows = addCoreSuitabilityQuestions(client, newFormRows)

    return [newFormRows]
  } else {
    // Store nextPath as a StoredQuestion
    saveQuestion(client, null, nextQuestionPath, page)

    // Evaluate the nextPath and return it as a FormRow
    newFormRows.push(toFormRow(evaluatePath(nextQuestionPath), client))
    return [newFormRows]
  }
}
const recommendationMap = new Map([
  ['TRAD', 'TraditionalIRA'],
  ['ROTH', 'RothIRA'],
  ['SEP', 'SimplifiedEmployeePension'],
])

export const getNextPath = (currentPath: string, client: ApolloClient<any>) => {
  // Determine if the current path contains additional questions.  If not,
  // return an empty string to indicate that there are no further processing.
  const questions = evaluatePath(`${currentPath}.questions`)
  if (!questions) {
    return ''
  }

  // Retrieve all StoredQuestion items from local state
  const { showCoreSuitabilityQuestions, storedQuestions, recommendation } = getLocalState(client)

  // If any of the questions at the current path have not been
  // answered, then return the path to that question
  for (let i = 0; i < questions.length; i++) {
    const matchingQuestion = storedQuestions.filter(
      question => question.questionId === questions[i].id,
    )

    if (matchingQuestion.length === 0) {
      return `${currentPath}.questions[${i}]`
    }
  }

  // Since all questions at the current path have been answered, evaluate all
  // conditions, and recursively call this function with the matching condition

  // Iterate through branches
  const branches = evaluatePath(`${currentPath}.branches`)
  for (let b = 0; branches.length; b++) {
    // If there are multiple conditions in this branch, then evaluate each one
    const conditions = evaluatePath(`${currentPath}.branches[${b}].conditions`)
    const condition = evaluatePath(`${currentPath}.branches[${b}].condition`)

    if (condition || conditions) {
      let conditionsMet = true

      if (conditions) {
        for (const c of conditions) {
          const answerId = c.answerId || c.value_num
          const storedAnswerId = getAnswer(client, c.questionId)
          if (!evaluateCondition(c.type, answerId, storedAnswerId)) {
            conditionsMet = false
            break
          }
        }
      } else {
        const answerId = condition.answerId || condition.value_num
        const storedAnswerId = getAnswer(client, condition.questionId)
        if (!evaluateCondition(condition.type, answerId, storedAnswerId)) {
          conditionsMet = false
        }
      }

      // If all conditions have been met, then this path is valid
      if (conditionsMet) {
        // Check for recommendation and showCoreSuitability marked and set
        // accordingly
        let newRecommendation = evaluatePath(`${currentPath}.branches[${b}].recommendation`)
        if (newRecommendation) {
          newRecommendation = recommendationMap.get(newRecommendation)
        }

        const newShowCoreSuitabilityQuestions = evaluatePath(
          `${currentPath}.branches[${b}].showCoreSuitabilityQuestions`,
        )

        if (newRecommendation || newShowCoreSuitabilityQuestions) {
          client.cache.writeData({
            data: {
              recommendation: newRecommendation || recommendation,
              showCoreSuitabilityQuestions:
                newShowCoreSuitabilityQuestions || showCoreSuitabilityQuestions,
            },
          })
        }

        if (
          !showCoreSuitabilityQuestions &&
          newShowCoreSuitabilityQuestions &&
          !areAllCoreSuitabilityQuestionsAnswered(client)
        ) {
          return 'showCoreSuitabilityQuestions'
        } else {
          // Recursively call this function with this path
          return getNextPath(`${currentPath}.branches[${b}].child`, client)
        }
      }
    }
  }
}

export const evaluateCondition = (
  conditionType: string,
  value: string,
  answer: string,
): boolean => {
  const answerValue = conditionType === 'eq' ? answer : parseInt(answer, 10)
  const conditionValue = conditionType === 'eq' ? value : parseInt(value, 10)

  switch (conditionType) {
    case 'eq':
      return answerValue === conditionValue
    case 'gt':
      return answerValue > conditionValue
    case 'gte':
      return answerValue >= conditionValue
    case 'lt':
      return answerValue < conditionValue
    case 'default':
      return true
    default:
      return false
  }
}
